import { useCallback, useMemo, useRef, useState } from "react";
import { getApi } from "@/api";
import { track } from "@/analytics/analytics";
import { env } from "@/config/env";
import type { IntakeAnswers, SubmitIntakeRequest, UploadedFileRef } from "@/types/api";
import type { IntakeRuntimeConfig } from "@/types/embed";
import type { FileUploadStep, IntakeStep, OfferConfig } from "@/types/offer";
import { fileExtension } from "@/utils/uploads";
import { uploadFileToSlot } from "@/utils/uploads";
import type { UploadItem } from "@/components/steps/types";
import { buildEngineCustomContext } from "@/lib/engineTrackingContext";
import { validateStep } from "./validation";

export type IntakePhase = "intake" | "submitting" | "submitted" | "error";

export interface IntakeMachine {
  offer: OfferConfig;
  runtime: IntakeRuntimeConfig;

  stepIndex: number;
  step: IntakeStep;
  totalSteps: number;
  /** 0..1 progress across the flow. */
  progress: number;

  answers: IntakeAnswers;
  setAnswer: (stepId: string, value: unknown) => void;

  uploadItems: UploadItem[];
  uploadedFiles: UploadedFileRef[];
  uploadsInProgress: boolean;
  addFiles: (step: FileUploadStep, files: File[]) => Promise<void>;
  removeUpload: (id: string) => void;

  currentError: string | null;
  showErrors: boolean;
  /** False only when the current step is actively blocking (e.g. uploading). */
  canAdvance: boolean;

  next: () => void;
  back: () => void;
  goTo: (index: number) => void;

  phase: IntakePhase;
  submitError: string | null;
  reportId: string | null;
  email: string;
  submit: () => Promise<void>;
}

function genId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function useIntakeMachine(
  offer: OfferConfig,
  runtime: IntakeRuntimeConfig,
): IntakeMachine {
  const offerId = runtime.offerId;
  if (!offerId) {
    throw new Error("Intake requires runtime.offerId.");
  }

  const api = useMemo(() => getApi(runtime.apiBaseUrl), [runtime.apiBaseUrl]);

  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<IntakeAnswers>({});
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [phase, setPhase] = useState<IntakePhase>("intake");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);

  // Guard against creating multiple intake records from concurrent callers.
  const startPromise = useRef<Promise<string> | null>(null);

  const step = offer.steps[stepIndex];
  const totalSteps = offer.steps.length;
  const progress = totalSteps > 0 ? (stepIndex + 1) / totalSteps : 0;

  const uploadedFiles = useMemo(
    () => uploadItems.filter((i) => i.status === "done" && i.ref).map((i) => i.ref as UploadedFileRef),
    [uploadItems],
  );
  const uploadsInProgress = uploadItems.some((i) => i.status === "uploading");

  const emailStep = offer.steps.find((s) => s.type === "email");
  const email = emailStep ? String(answers[emailStep.id] ?? "") : "";

  const currentError = useMemo(
    () => validateStep(step, answers[step.id], { uploadedFiles, uploadsInProgress }),
    [step, answers, uploadedFiles, uploadsInProgress],
  );

  const canAdvance = !(step.type === "fileUpload" && uploadsInProgress);

  const setAnswer = useCallback(
    (stepId: string, value: unknown) => {
      setAnswers((prev) => ({ ...prev, [stepId]: value }));
      setShowErrors(false);
      track("question_answered", { stepId });
    },
    [],
  );

  const goTo = useCallback((index: number) => {
    setShowErrors(false);
    setStepIndex(index);
  }, []);

  const next = useCallback(() => {
    const err = validateStep(step, answers[step.id], { uploadedFiles, uploadsInProgress });
    if (err) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStepIndex((i) => Math.min(offer.steps.length - 1, i + 1));
  }, [step, answers, uploadedFiles, uploadsInProgress, offer.steps.length]);

  const back = useCallback(() => {
    setShowErrors(false);
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const ensureStarted = useCallback(async (): Promise<string> => {
    if (reportId) return reportId;
    if (!startPromise.current) {
      startPromise.current = api
        .startIntake({
          offerId,
          campaignId: runtime.campaignId,
          source: runtime.source,
          utm: runtime.utm,
        })
        .then((r) => {
          setReportId(r.reportId);
          track("intake_started", { offerId, reportId: r.reportId });
          return r.reportId;
        });
    }
    return startPromise.current;
  }, [api, offerId, reportId, runtime.campaignId, runtime.source, runtime.utm]);

  const addFiles = useCallback(
    async (fileStep: FileUploadStep, files: File[]) => {
      // Client-side guards before requesting upload slots.
      const allowed = fileStep.allowedFileTypes?.map((t) => t.toLowerCase());
      const maxBytes = fileStep.maxFileSizeMb ? fileStep.maxFileSizeMb * 1024 * 1024 : undefined;
      const remaining = fileStep.maxFiles
        ? Math.max(0, fileStep.maxFiles - uploadItems.length)
        : files.length;
      const accepted: File[] = [];
      const rejected: UploadItem[] = [];

      for (const file of files.slice(0, remaining)) {
        if (allowed && !allowed.includes(fileExtension(file.name))) {
          rejected.push({
            id: genId(),
            fileName: file.name,
            sizeBytes: file.size,
            progress: 0,
            status: "error",
            error: "Unsupported file type",
          });
        } else if (maxBytes && file.size > maxBytes) {
          rejected.push({
            id: genId(),
            fileName: file.name,
            sizeBytes: file.size,
            progress: 0,
            status: "error",
            error: `File exceeds ${fileStep.maxFileSizeMb} MB`,
          });
        } else {
          accepted.push(file);
        }
      }

      const newItems: UploadItem[] = accepted.map((f) => ({
        id: genId(),
        fileName: f.name,
        sizeBytes: f.size,
        progress: 0,
        status: "uploading",
      }));
      setUploadItems((prev) => [...prev, ...newItems, ...rejected]);

      if (accepted.length === 0) return;

      try {
        const id = await ensureStarted();
        const session = await api.createUploadSession({
          reportId: id,
          files: accepted.map((f) => ({
            fileName: f.name,
            contentType: f.type,
            sizeBytes: f.size,
          })),
        });

        await Promise.all(
          accepted.map(async (file, idx) => {
            const slot = session.uploads[idx];
            const itemId = newItems[idx].id;
            if (!slot) {
              setUploadItems((prev) =>
                prev.map((it) =>
                  it.id === itemId ? { ...it, status: "error", error: "No upload slot" } : it,
                ),
              );
              return;
            }
            try {
              await uploadFileToSlot(
                file,
                slot,
                (p) => {
                  setUploadItems((prev) =>
                    prev.map((it) => (it.id === itemId ? { ...it, progress: p.fraction } : it)),
                  );
                },
                env.useMockApi ? undefined : { reportId: id, apiBaseUrl: env.apiBaseUrl },
              );

              if (!env.useMockApi) {
                await api.validateUpload({
                  reportId: id,
                  blobName: slot.blobName,
                  fileName: file.name,
                });
              }

              const ref: UploadedFileRef = {
                fileName: file.name,
                blobName: slot.blobName,
                contentType: file.type,
                sizeBytes: file.size,
              };
              setUploadItems((prev) =>
                prev.map((it) =>
                  it.id === itemId ? { ...it, status: "done", progress: 1, ref } : it,
                ),
              );
              track("file_uploaded", { reportId: id, fileName: file.name, sizeBytes: file.size });
            } catch (e) {
              setUploadItems((prev) =>
                prev.map((it) =>
                  it.id === itemId
                    ? { ...it, status: "error", error: (e as Error).message }
                    : it,
                ),
              );
            }
          }),
        );
      } catch (e) {
        setUploadItems((prev) =>
          prev.map((it) =>
            newItems.some((n) => n.id === it.id)
              ? { ...it, status: "error", error: (e as Error).message }
              : it,
          ),
        );
      }
    },
    [api, ensureStarted, uploadItems.length],
  );

  const removeUpload = useCallback((id: string) => {
    setUploadItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const submit = useCallback(async () => {
    setSubmitError(null);
    setPhase("submitting");
    try {
      const id = await ensureStarted();
      const payload: SubmitIntakeRequest = {
        reportId: id,
        offerId,
        campaignId: runtime.campaignId,
        source: runtime.source,
        utm: runtime.utm,
        answers,
        uploadedFiles,
        email,
        createdAt: new Date().toISOString(),
        customContext: buildEngineCustomContext(offer.pipeline, id),
      };
      await api.submitIntake(payload);
      track("intake_submitted", { reportId: id, offerId });
      setPhase("submitted");
    } catch (e) {
      setSubmitError((e as Error).message);
      setPhase("error");
    }
  }, [api, ensureStarted, offerId, runtime, offer.pipeline, answers, uploadedFiles, email]);

  return {
    offer,
    runtime,
    stepIndex,
    step,
    totalSteps,
    progress,
    answers,
    setAnswer,
    uploadItems,
    uploadedFiles,
    uploadsInProgress,
    addFiles,
    removeUpload,
    currentError,
    showErrors,
    canAdvance,
    next,
    back,
    goTo,
    phase,
    submitError,
    reportId,
    email,
    submit,
  };
}
